import type {
  AccountResult,
  ApiEnvelope,
  ApifyRunInfo,
  ArchiveEvent,
  AssetCondition,
  Category,
  CollectionRun,
  GuidelineFolder,
  GuidelineStep,
  GuidelineTool,
  KeywordTrend,
  Me,
  NavigationResponse,
  Permission,
  Platform,
  PlatformFilter,
  PostResult,
  RecommendationRun,
  UserRecord,
} from './types';

const REMOTE_API_BASE = 'https://pick7reels-worker.artubeditor.workers.dev';
const LOCAL_API_BASE = 'http://127.0.0.1:8787';
const API_BASE = import.meta.env.VITE_PICK7REELS_API_BASE || (import.meta.env.DEV ? LOCAL_API_BASE : REMOTE_API_BASE);

function devEmailHeader(): Record<string, string> {
  if (!import.meta.env.DEV) return {};
  const email = window.localStorage.getItem('pick7reels.devEmail') || '';
  return email ? { 'X-Debug-Email': email } : {};
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  for (const [key, value] of Object.entries(devEmailHeader())) headers.set(key, value);

  const response = await fetch(`${API_BASE}${path}`, { ...init, headers, credentials: 'include' });
  const envelope = (await response.json().catch(() => null)) as ApiEnvelope<T> | null;
  if (!response.ok || !envelope?.ok) {
    const message = envelope?.error?.message || `API ${response.status}`;
    throw new Error(message);
  }
  return envelope.data as T;
}

function toQuery(params: object): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params as Record<string, unknown>)) {
    if (value === undefined || value === null || value === '') continue;
    query.set(key, String(value));
  }
  const text = query.toString();
  return text ? `?${text}` : '';
}

export const api = {
  // ── auth ──
  login: (email: string, passphrase: string) => request<{ email: string; role: string }>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, passphrase }),
  }),
  logout: () => request<null>('/api/auth/logout', { method: 'POST' }),

  // ── shell ──
  me: () => request<Me>('/api/me'),
  navigation: () => request<NavigationResponse>('/api/navigation'),
  categories: () => request<{ categories: Category[] }>('/api/categories'),

  // ── users / permissions (admin) ──
  users: () => request<{ users: UserRecord[] }>('/api/users'),
  createUser: (body: Record<string, unknown>) => request<{ user: UserRecord }>('/api/users', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  updateUser: (id: number, body: Record<string, unknown>) => request<{ user: UserRecord }>(`/api/users/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }),
  addUserPermission: (id: number, body: Record<string, unknown>) => request<{ permissions: Permission[] }>(`/api/users/${id}/permissions`, {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  deleteUserPermission: (userId: number, permissionId: number) => request<{ permissions: Permission[] }>(`/api/users/${userId}/permissions/${permissionId}`, {
    method: 'DELETE',
  }),

  // ── keys (Apify token storage) ──
  keys: () => request<Array<{ id: number; label: string; status: string; maskedKey: string; storage: string }>>('/api/keys'),
  createKey: (body: { key: string; label?: string }) => request<{ id: number; label: string; status: string; maskedKey: string; storage: string }>('/api/keys', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  deleteKey: (id: number) => request<null>(`/api/keys/${id}`, { method: 'DELETE' }),

  // ── settings (admin) ──
  settings: () => request<{ settings: Record<string, unknown> }>('/api/settings'),
  updateSetting: (key: string, value: unknown) => request<{ key: string; value: unknown }>(`/api/settings/${encodeURIComponent(key)}`, {
    method: 'PATCH',
    body: JSON.stringify({ value }),
  }),

  // ── assets (collection conditions, admin) ──
  assets: (params: { target?: string; type?: string; editorEmail?: string } = {}) => request<{ conditions: AssetCondition[] }>(`/api/assets/conditions${toQuery(params)}`),
  createAsset: (body: Record<string, unknown>) => request<{ condition: AssetCondition }>('/api/assets/conditions', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  updateAsset: (id: number, body: Record<string, unknown>) => request<{ condition: AssetCondition }>(`/api/assets/conditions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }),
  deleteAsset: (id: number) => request<{ id: number; isActive: false }>(`/api/assets/conditions/${id}`, { method: 'DELETE' }),

  // ── collection runs (admin) ──
  collectionRuns: () => request<{ runs: CollectionRun[]; total: number }>('/api/collection/runs'),
  collectionRun: (id: number) => request<{ run: CollectionRun }>(`/api/collection/runs/${id}`),
  createCollectionRun: (body: { target: string; editorEmail: string; platform: PlatformFilter; categoryKey: string }) => request<{ run: CollectionRun }>('/api/collection/runs', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  collectionPosts: (params: Record<string, string | number> = {}) => request<{ posts: PostResult[]; total: number }>(`/api/collection/posts${toQuery(params)}`),

  // ── social discovery / collection (admin write, editor read) ──
  socialDiscover: (body: { platform: PlatformFilter | 'both'; query: string; limit?: number; dateRange?: string; sort?: string }) =>
    request<{ run?: ApifyRunInfo; items?: PostResult[] }>('/api/social/discover', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  collectHashtag: (body: { platform: Platform; hashtag: string; limit?: number }) =>
    request<{ run: ApifyRunInfo }>('/api/social/collect/hashtag', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  collectAccount: (body: { platform: Platform; username: string; limit?: number }) =>
    request<{ run: ApifyRunInfo }>('/api/social/collect/account', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  collectUrl: (body: { platform: Platform; url: string }) =>
    request<{ post: PostResult }>('/api/social/collect/url', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ── social read surfaces (editor) ──
  socialPosts: (params: {
    platform?: PlatformFilter;
    q?: string;
    keyword?: string;
    username?: string;
    accountId?: string;
    minViews?: number;
    postedWithin?: string;
    sort?: string;
    offset?: number;
    limit?: number;
  } = {}) => request<{ posts: PostResult[]; total: number }>(`/api/social/posts${toQuery(params)}`),
  socialAccounts: (params: {
    platform?: PlatformFilter;
    q?: string;
    minFollowers?: number;
    sort?: string;
    offset?: number;
    limit?: number;
  } = {}) => request<{ accounts: AccountResult[]; total: number }>(`/api/social/accounts${toQuery(params)}`),
  socialKeywords: (params: {
    platform?: PlatformFilter;
    type?: string;
    window?: string;
    sort?: string;
  } = {}) => request<{ keywords: KeywordTrend[] }>(`/api/social/keywords${toQuery(params)}`),
  socialTrends: (params: {
    entity: 'post' | 'keyword' | 'account';
    platform?: PlatformFilter;
    window?: '24h' | '7d' | '30d';
    limit?: number;
  }) => request<{ items: Array<PostResult | KeywordTrend | AccountResult>; computedAt: number }>(`/api/social/trends${toQuery(params)}`),

  // ── job runs (admin) ──
  socialJobs: (params: { status?: string; platform?: PlatformFilter; limit?: number; offset?: number } = {}) =>
    request<{ runs: ApifyRunInfo[]; total: number }>(`/api/social/jobs${toQuery(params)}`),
  socialJob: (id: number) => request<{ run: ApifyRunInfo; items?: PostResult[] }>(`/api/social/jobs/${id}`),
  recomputeScores: (body: { windows?: string[]; entityTypes?: string[]; platforms?: string[]; full?: boolean } = {}) =>
    request<Record<string, unknown>>('/api/social/recompute-scores', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ── recommendations (editor read / admin write) ──
  recommendationRuns: () => request<{ runs: RecommendationRun[]; total: number }>('/api/recommendations'),
  recommendationRun: (id: number) => request<{ run: RecommendationRun; items: PostResult[] }>(`/api/recommendations/${id}`),
  generateRecommendation: (body: Record<string, unknown>) => request<{ run: RecommendationRun; items: PostResult[]; skipped?: boolean; reason?: string }>('/api/recommendations/generate', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  regenerateRecommendation: (body: Record<string, unknown>) => request<{ run: RecommendationRun; items: PostResult[]; skipped?: boolean; reason?: string }>('/api/recommendations/regenerate', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  addRecommendationItem: (runId: number, body: Record<string, unknown>) => request<{ run: RecommendationRun; item: PostResult }>(`/api/recommendations/${runId}/items`, {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  updateRecommendationItem: (id: number, body: Record<string, unknown>) => request<{ item: PostResult }>(`/api/recommendations/items/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }),
  recommendationToExample: (id: number) => request<{ item: PostResult; duplicated: boolean }>(`/api/recommendations/items/${id}/example`, { method: 'POST' }),
  recommendationToArchive: (id: number) => request<{ item: PostResult; duplicated: boolean }>(`/api/recommendations/items/${id}/archive`, { method: 'POST' }),
  archiveRecommendationRun: (runId: number) => request<{ archived: number }>(`/api/recommendations/${runId}/archive`, { method: 'POST' }),

  // ── examples (editor) ──
  examples: (sort = 'latest') => request<{ items: PostResult[]; total: number }>(`/api/examples?sort=${encodeURIComponent(sort)}`),
  updateExample: (id: number, body: Record<string, unknown>) => request<{ item: PostResult }>(`/api/examples/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }),
  deleteExample: (id: number) => request<null>(`/api/examples/${id}`, { method: 'DELETE' }),

  // ── archive (editor) ──
  archive: (params: Record<string, string | number> = {}) => request<{ items: PostResult[]; total: number }>(`/api/archive${toQuery(params)}`),
  addArchive: (body: Record<string, unknown>) => request<{ item: PostResult; duplicated: boolean }>('/api/archive', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  updateArchive: (id: number, body: Record<string, unknown>) => request<{ item: PostResult }>(`/api/archive/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }),
  archiveEvents: (id: number) => request<{ events: ArchiveEvent[] }>(`/api/archive/${id}/events`),
  deleteArchive: (id: number) => request<null>(`/api/archive/${id}`, { method: 'DELETE' }),

  // ── guidelines (editor read / admin write) ──
  guidelines: () => request<{ steps: GuidelineStep[]; toolFolders: GuidelineFolder[] }>('/api/guidelines'),
  createGuidelineStep: (body: Record<string, unknown>) => request<{ step: GuidelineStep }>('/api/guidelines/steps', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  updateGuidelineStep: (id: number, body: Record<string, unknown>) => request<{ item: GuidelineStep }>(`/api/guidelines/steps/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }),
  createToolFolder: (body: Record<string, unknown>) => request<{ folder: GuidelineFolder }>('/api/guidelines/tool-folders', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  updateToolFolder: (id: number, body: Record<string, unknown>) => request<{ item: GuidelineFolder }>(`/api/guidelines/tool-folders/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }),
  createGuidelineTool: (body: Record<string, unknown>) => request<{ tool: GuidelineTool }>('/api/guidelines/tools', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  updateGuidelineTool: (id: number, body: Record<string, unknown>) => request<{ item: GuidelineTool }>(`/api/guidelines/tools/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }),
};

export async function pollJob(runId: number, opts: {
  intervalMs?: number;
  timeoutMs?: number;
  onTick?: (run: ApifyRunInfo) => void;
  signal?: { cancelled: boolean };
} = {}): Promise<ApifyRunInfo> {
  const { intervalMs = 2500, timeoutMs = 300000, onTick, signal } = opts;
  const start = Date.now();
  for (;;) {
    if (signal?.cancelled) throw new Error('polling cancelled');
    const { run } = await api.socialJob(runId);
    onTick?.(run);
    if (['succeeded', 'failed', 'aborted', 'timeout'].includes(run.status)) return run;
    if (Date.now() - start > timeoutMs) throw new Error('수집 시간 초과 — 실행 기록에서 상태를 확인해주세요');
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
}
